import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { listBrands, createBrand, deleteBrand, updateBrand, reorderBrands } from "../../api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import {
    Typography, Button, Alert, Card, CardContent, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, IconButton, CircularProgress, Tooltip, Snackbar, CardActionArea
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import Grid from "@mui/material/Grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";

import ImagePicker from "../../components/imagepicker/ImagePicker";
import { toImgSrcKey } from "../../utils/cdn";
import { reorderList } from "../../utils/reorderList";
import { blackIconTheme } from "../../theme/blackIconTheme";

function toKey(x) {
    if (!x) return "";
    if (typeof x === "string") return x.replace(/^\/+/, "");
    return String(x.key || x.url || "").replace(/^\/+/, "");
}

export default function BrandsPage() {
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [isReordering, setIsReordering] = useState(false);
    const reorderInFlightRef = useRef(false);

    const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });

    const [createOpen, setCreateOpen] = useState(false);
    const [createSaving, setCreateSaving] = useState(false);
    const [createData, setCreateData] = useState({ name: "", image: "" });

    const [editOpen, setEditOpen] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editData, setEditData] = useState(null);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteSaving, setDeleteSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            const data = await listBrands();
            setBrands(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e?.message || "Failed to load brands");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const onCreate = async () => {
        if (!createData.name || createData.name.trim().length < 2) {
            setErr("Brand name must be at least 2 characters");
            return;
        }

        try {
            setCreateSaving(true);
            setErr("");
            await createBrand({
                name: createData.name.trim(),
                image: toKey(createData.image) || null,
            });
            setCreateOpen(false);
            setCreateData({ name: "", image: "" });
            await refresh();
            setNotice({ open: true, message: "Brand created", severity: "success" });
        } catch (e) {
            setErr(e?.message || "Failed to create brand");
            setNotice({ open: true, message: e?.message || "Failed to create brand", severity: "error" });
        } finally {
            setCreateSaving(false);
        }
    };

    const onSaveEdit = async () => {
        if (!editData?.id) return;

        try {
            setEditSaving(true);
            setErr("");
            await updateBrand(editData.id, {
                name: String(editData.name || "").trim(),
                image: toKey(editData.image) || null,
            });
            setEditOpen(false);
            await refresh();
            setNotice({ open: true, message: "Brand updated", severity: "success" });
        } catch (e) {
            setErr(e?.message || "Failed to update brand");
            setNotice({ open: true, message: e?.message || "Failed to update brand", severity: "error" });
        } finally {
            setEditSaving(false);
        }
    };

    const askDeleteBrand = useCallback((brand) => {
        if (!brand) return;
        if (Number(brand.productCount || 0) > 0) {
            setErr("Cannot delete brand with products");
            setNotice({ open: true, message: "Cannot delete brand with products", severity: "error" });
            return;
        }
        setDeleteTarget(brand);
        setDeleteOpen(true);
    }, []);

    const confirmDeleteBrand = useCallback(async () => {
        if (!deleteTarget?.id) return;

        try {
            setDeleteSaving(true);
            setErr("");
            await deleteBrand(deleteTarget.id);
            setDeleteOpen(false);
            setDeleteTarget(null);
            await refresh();
            setNotice({ open: true, message: "Brand deleted", severity: "success" });
        } catch (e) {
            if (e?.status === 400) {
                setErr(e?.message || "Cannot delete brand with products");
            } else {
                setErr(e?.message || "Failed to delete brand");
            }
            setNotice({ open: true, message: e?.message || "Failed to delete brand", severity: "error" });
        } finally {
            setDeleteSaving(false);
        }
    }, [deleteTarget, refresh]);

    const saveReorder = useCallback(async (next, previous) => {
        const prevIds = previous.map((brand) => brand.id);
        const nextIds = next.map((brand) => brand.id);

        if (JSON.stringify(prevIds) === JSON.stringify(nextIds)) return;
        if (reorderInFlightRef.current) return;

        reorderInFlightRef.current = true;
        setIsReordering(true);
        try {
            await reorderBrands(nextIds);
            setNotice({ open: true, message: "Brand order saved", severity: "success" });
        } catch (e) {
            setErr(e?.message || "Failed to save brand order");
            setBrands(previous);
            setNotice({ open: true, message: e?.message || "Failed to save brand order", severity: "error" });
        } finally {
            reorderInFlightRef.current = false;
            setIsReordering(false);
        }
    }, []);

    const moveBrand = useCallback(async (from, to) => {
        if (isReordering || reorderInFlightRef.current) return;
        if (to < 0 || to >= brands.length || from === to) return;

        const previous = brands;
        const next = reorderList(brands, from, to);
        setBrands(next);
        await saveReorder(next, previous);
    }, [brands, isReordering, saveReorder]);

    const handleDragEnd = useCallback(async (result) => {
        if (reorderInFlightRef.current) return;
        const { source, destination } = result;
        if (!destination) return;
        if (source.index === destination.index) return;
        await moveBrand(source.index, destination.index);
    }, [moveBrand]);

    const onBrandKeyDown = useCallback((event, index) => {
        if (isReordering || !event.altKey) return;

        if (event.key === "ArrowUp") {
            event.preventDefault();
            moveBrand(index, index - 1);
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            moveBrand(index, index + 1);
        }
    }, [isReordering, moveBrand]);

    const openBrand = useCallback((brandId) => {
        const id = String(brandId ?? "");
        if (!id) return;
        navigate(`/brands/${encodeURIComponent(id)}`);
    }, [navigate]);

    const controlsDisabled = loading || isReordering || createSaving || editSaving || deleteSaving;

    return (
        <ThemeProvider theme={blackIconTheme}>
            <div>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="h5">Brands</Typography>
                        {(loading || isReordering) && <CircularProgress size={16} />}
                        {loading && (
                            <Typography variant="body2" color="text.secondary">
                                Loading...
                            </Typography>
                        )}
                        {isReordering && (
                            <Typography variant="body2" color="text.secondary">
                                Saving order...
                            </Typography>
                        )}
                    </Stack>
                    <Button variant="contained" onClick={() => setCreateOpen(true)} disabled={controlsDisabled}>
                        New brand
                    </Button>
                </Stack>

                {err && <Alert severity="error">{err}</Alert>}

                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="brands">
                            {(dropProvided, dropSnapshot) => (
                                <Grid
                                    container
                                    spacing={2}
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                >
                                    {brands.map((b, index) => {
                                        const src = b.image ? toImgSrcKey(b.image) : "";
                                        const hasProducts = Number(b.productCount || 0) > 0;

                                        return (
                                            <Draggable key={b.id} draggableId={b.id} index={index} isDragDisabled={isReordering}>
                                                {(dragProvided) => (
                                                    <Grid
                                                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        style={{
                                                            ...dragProvided.draggableProps.style,
                                                            transition: "transform 200ms ease",
                                                        }}
                                                    >
                                                        <Card
                                                            tabIndex={0}
                                                            onKeyDown={(e) => onBrandKeyDown(e, index)}
                                                            sx={{
                                                                outline: "none",
                                                                '&:focus-visible': {
                                                                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                                                                },
                                                            }}
                                                        >
                                                            <CardContent>
                                                                <CardActionArea
                                                                    onClick={() => openBrand(b.id)}
                                                                    sx={{ borderRadius: 1, p: 0.5 }}
                                                                >
                                                                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                                            <Avatar src={src}>{b.name?.[0]}</Avatar>
                                                                            <Stack spacing={0.5}>
                                                                                <Typography>{b.name}</Typography>
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    {Number(b.productCount || 0)} products
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Stack>

                                                                        <IconButton
                                                                            {...dragProvided.dragHandleProps}
                                                                            aria-label={`Drag ${b.name}`}
                                                                            disabled={isReordering}
                                                                            size="small"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            sx={{ cursor: isReordering ? "not-allowed" : "grab" }}
                                                                        >
                                                                            <DragIndicatorIcon />
                                                                        </IconButton>
                                                                    </Stack>
                                                                </CardActionArea>

                                                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                                                                    <Stack direction="row">
                                                                        <IconButton
                                                                            onClick={() => moveBrand(index, index - 1)}
                                                                            disabled={isReordering || index === 0}
                                                                            size="small"
                                                                            aria-label="Move up"
                                                                        >
                                                                            <ArrowUpwardIcon fontSize="small" />
                                                                        </IconButton>

                                                                        <IconButton
                                                                            onClick={() => moveBrand(index, index + 1)}
                                                                            disabled={isReordering || index === brands.length - 1}
                                                                            size="small"
                                                                            aria-label="Move down"
                                                                        >
                                                                            <ArrowDownwardIcon fontSize="small" />
                                                                        </IconButton>

                                                                        <IconButton
                                                                            onClick={() => moveBrand(index, 0)}
                                                                            disabled={isReordering || index === 0}
                                                                            size="small"
                                                                            aria-label="Move to top"
                                                                        >
                                                                            <VerticalAlignTopIcon fontSize="small" />
                                                                        </IconButton>

                                                                        <IconButton
                                                                            onClick={() => moveBrand(index, brands.length - 1)}
                                                                            disabled={isReordering || index === brands.length - 1}
                                                                            size="small"
                                                                            aria-label="Move to bottom"
                                                                        >
                                                                            <VerticalAlignBottomIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Stack>

                                                                    <Stack direction="row" spacing={0.5}>
                                                                        <Button
                                                                            component={RouterLink}
                                                                            to={`/brands/${encodeURIComponent(String(b.id || ""))}`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        >
                                                                            Manage products
                                                                        </Button>
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                setEditData({ ...b });
                                                                                setEditOpen(true);
                                                                            }}
                                                                            disabled={isReordering}
                                                                        >
                                                                            <EditOutlinedIcon />
                                                                        </IconButton>

                                                                        <Tooltip title={hasProducts ? "Cannot delete brand with products" : "Delete brand"}>
                                                                            <span>
                                                                                <IconButton
                                                                                    onClick={() => askDeleteBrand(b)}
                                                                                    disabled={isReordering || hasProducts}
                                                                                >
                                                                                    <DeleteOutlineIcon />
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {dropSnapshot.isDraggingOver && (
                                        <Grid size={{ xs: 12 }}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    minHeight: 72,
                                                    borderStyle: "dashed",
                                                    borderColor: "#000",
                                                    bgcolor: "rgba(0,0,0,0.04)",
                                                }}
                                            />
                                        </Grid>
                                    )}
                                    {dropProvided.placeholder}
                                </Grid>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}

                <Dialog open={createOpen} onClose={() => !createSaving && setCreateOpen(false)} fullWidth>
                    <DialogTitle>New brand</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Name"
                            value={createData.name}
                            onChange={(e) => setCreateData((p) => ({ ...p, name: e.target.value }))}
                            disabled={createSaving}
                        />

                        <ImagePicker
                            single
                            value={createData.image ? [createData.image] : []}
                            onChange={(updater) => {
                                setCreateData((prev) => {
                                    const base = prev.image ? [prev.image] : [];
                                    const next = typeof updater === "function" ? updater(base) : updater;

                                    return {
                                        ...prev,
                                        image: toKey(next?.[0]),
                                    };
                                });
                            }}
                            folder="brands"
                            libraryPrefix="brands"
                        />
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setCreateOpen(false)} disabled={createSaving}>Cancel</Button>
                        <Button onClick={onCreate} disabled={createSaving}>
                            {createSaving ? "Creating..." : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={editOpen} onClose={() => !editSaving && setEditOpen(false)} fullWidth>
                    <DialogTitle>Edit brand</DialogTitle>
                    <DialogContent>
                        {editData && (
                            <>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    value={editData.name}
                                    onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))}
                                    disabled={editSaving}
                                />

                                <ImagePicker
                                    single
                                    value={editData.image ? [editData.image] : []}
                                    onChange={(updater) => {
                                        setEditData((prev) => {
                                            const base = prev.image ? [prev.image] : [];
                                            const next = typeof updater === "function" ? updater(base) : updater;

                                            return {
                                                ...prev,
                                                image: toKey(next?.[0]),
                                            };
                                        });
                                    }}
                                    folder="brands"
                                    libraryPrefix="brands"
                                />
                            </>
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>
                        <Button onClick={onSaveEdit} disabled={editSaving || !editData?.name?.trim()}>
                            {editSaving ? "Saving..." : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={deleteOpen} onClose={() => !deleteSaving && setDeleteOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Delete brand</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Delete brand "{deleteTarget?.name || ''}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteOpen(false)} disabled={deleteSaving}>Cancel</Button>
                        <Button color="error" onClick={confirmDeleteBrand} disabled={deleteSaving}>
                            {deleteSaving ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={notice.open}
                    autoHideDuration={3000}
                    onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
                        severity={notice.severity}
                        sx={{ width: "100%" }}
                    >
                        {notice.message}
                    </Alert>
                </Snackbar>
            </div>
        </ThemeProvider>
    );
}
